<template>
  <div class="alice-gst">
    <header class="alice-gst__head">
      <div class="alice-gst__head-main">
        <div class="alice-gst__crumb">
          <span>Admin</span>
          <Icon name="chevron-right" :size="12" />
          <span>Guests</span>
        </div>
        <h1 class="alice-gst__title">
          Guest access
          <span class="alice-gst__count">{{ grants.length }}</span>
        </h1>
        <p class="alice-gst__subtitle">
          External researchers invited to a single project. Access is time-boxed
          (max 60 days) and token-budgeted; disabling or revoking cuts data
          access immediately.
        </p>
      </div>
      <button class="alice-gst__refresh" :disabled="loading" @click="load">
        {{ loading ? "Loading…" : "Refresh" }}
      </button>
    </header>

    <div v-if="loading && !grants.length" class="alice-gst__state">
      Loading guests…
    </div>
    <div v-else-if="error" class="alice-gst__state alice-gst__state--error">
      {{ error }}
    </div>
    <div v-else-if="!grants.length" class="alice-gst__state">
      No guests yet. Researchers invite guests from a project's ⋯ menu ("Invite
      guest…").
    </div>

    <div v-else class="alice-gst__table">
      <div class="alice-gst__row alice-gst__row--head">
        <div>Guest</div>
        <div>Project</div>
        <div>Invited by</div>
        <div>Access ends</div>
        <div>Token usage</div>
        <div>Status</div>
        <div></div>
      </div>

      <template v-for="g in grants" :key="g.id">
        <div class="alice-gst__row">
          <div class="alice-gst__email" :title="g.email">{{ g.email }}</div>
          <div class="alice-gst__project" :title="g.projectTitle">
            <router-link :to="`/project/${g.projectId}/chat`">{{
              g.projectTitle || g.projectId
            }}</router-link>
          </div>
          <div class="alice-gst__inviter" :title="g.invitedBy?.email">
            {{ inviterLabel(g) }}
          </div>
          <div>
            {{ formatDate(g.expiresAt) }}
            <div class="alice-gst__sub">{{ relativeExpiry(g) }}</div>
          </div>
          <div>
            <div class="alice-gst__usage-bar">
              <span
                class="alice-gst__usage-fill"
                :class="{ 'alice-gst__usage-fill--full': usagePct(g) >= 100 }"
                :style="{ width: Math.min(usagePct(g), 100) + '%' }"
              />
            </div>
            <div class="alice-gst__sub">
              {{ formatTokens(g.tokensUsed) }} /
              {{ formatTokens(g.tokenBudget) }} ({{ usagePct(g) }}%)
            </div>
          </div>
          <div>
            <span
              class="alice-gst__status"
              :class="`alice-gst__status--${g.displayStatus}`"
              >{{ g.displayStatus }}</span
            >
          </div>
          <div class="alice-gst__actions">
            <button
              class="alice-gst__action"
              title="Extend access / change budget"
              @click="toggleEdit(g)"
            >
              <Icon name="pencil" :size="13" />
            </button>
            <button
              v-if="['invited', 'active'].includes(g.displayStatus)"
              class="alice-gst__action"
              title="Disable (guest is blocked until re-enabled)"
              @click="act(g, 'disable')"
            >
              Disable
            </button>
            <button
              v-else-if="g.displayStatus === 'disabled'"
              class="alice-gst__action"
              title="Re-enable"
              @click="act(g, 'enable')"
            >
              Enable
            </button>
            <button
              v-if="g.displayStatus !== 'revoked'"
              class="alice-gst__action alice-gst__action--danger"
              title="Revoke access permanently"
              @click="confirmRevoke(g)"
            >
              Revoke
            </button>
            <button
              class="alice-gst__action"
              title="Copy invite message (also works as a resend)"
              @click="copyInvite(g)"
            >
              {{ copiedId === g.id ? "Copied!" : "Copy invite" }}
            </button>
          </div>
        </div>

        <div v-if="editingId === g.id" class="alice-gst__edit">
          <label class="alice-gst__edit-field">
            <span>Access ends</span>
            <input
              v-model="editDate"
              type="date"
              :min="minDate"
              :max="maxDate"
            />
          </label>
          <label class="alice-gst__edit-field">
            <span>Token budget</span>
            <input
              v-model.number="editBudget"
              type="number"
              min="1"
              step="100000"
            />
          </label>
          <button
            class="alice-gst__edit-save"
            :disabled="busy"
            @click="saveEdit(g)"
          >
            Save
          </button>
          <button class="alice-gst__edit-cancel" @click="editingId = null">
            Cancel
          </button>
        </div>
      </template>
    </div>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
      location="bottom right"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import { httpsCallable } from "firebase/functions";
import { functions } from "@/firebase";
import Icon from "@/components/alice/Icon.vue";

const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_DAYS = 60;

export default {
  name: "AdminGuests",
  components: { Icon },
  data() {
    return {
      grants: [],
      loading: false,
      busy: false,
      error: null,
      editingId: null,
      editDate: "",
      editBudget: null,
      copiedId: null,
      snackbar: { show: false, text: "", color: "success" },
    };
  },
  computed: {
    minDate() {
      return new Date(Date.now() + DAY_MS).toISOString().slice(0, 10);
    },
    maxDate() {
      return new Date(Date.now() + MAX_DAYS * DAY_MS)
        .toISOString()
        .slice(0, 10);
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const listGuestGrants = httpsCallable(functions, "listGuestGrants");
        const res = await listGuestGrants();
        this.grants = res.data?.grants || [];
      } catch (err) {
        console.error("listGuestGrants failed:", err);
        this.error = "Couldn't load guest grants (admin only).";
      } finally {
        this.loading = false;
      }
    },
    inviterLabel(g) {
      const email = g.invitedBy?.email || "";
      return email.split("@")[0] || "—";
    },
    formatDate(ms) {
      if (!ms) return "—";
      return new Date(ms).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    relativeExpiry(g) {
      if (!g.expiresAt) return "";
      const days = Math.ceil((g.expiresAt - Date.now()) / DAY_MS);
      if (days < 0) return `${Math.abs(days)}d ago`;
      if (days === 0) return "today";
      return `in ${days}d`;
    },
    usagePct(g) {
      const budget = g.tokenBudget || 1;
      return Math.round(((g.tokensUsed || 0) / budget) * 100);
    },
    formatTokens(n) {
      const v = n || 0;
      if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
      if (v >= 1_000) return `${Math.round(v / 1_000)}k`;
      return String(v);
    },
    toggleEdit(g) {
      if (this.editingId === g.id) {
        this.editingId = null;
        return;
      }
      this.editingId = g.id;
      this.editDate = g.expiresAt
        ? new Date(g.expiresAt).toISOString().slice(0, 10)
        : this.minDate;
      this.editBudget = g.tokenBudget || 2000000;
    },
    async saveEdit(g) {
      this.busy = true;
      try {
        if (this.editDate) {
          const [y, m, d] = this.editDate.split("-").map(Number);
          const endOfDay = new Date(y, m - 1, d, 23, 59, 59).getTime();
          if (endOfDay !== g.expiresAt) {
            await this.callUpdate(g.id, "extend", endOfDay);
          }
        }
        if (this.editBudget && this.editBudget !== g.tokenBudget) {
          await this.callUpdate(g.id, "setBudget", this.editBudget);
        }
        this.editingId = null;
        this.notify("Grant updated.");
        await this.load();
      } catch (err) {
        console.error("saveEdit failed:", err);
        this.notify(this.friendlyError(err), "error");
      } finally {
        this.busy = false;
      }
    },
    confirmRevoke(g) {
      if (
        !confirm(
          `Revoke ${g.email}'s access to "${g.projectTitle}"?\n\nTheir account is disabled immediately. Re-inviting from the project creates fresh access.`
        )
      ) {
        return;
      }
      this.act(g, "revoke");
    },
    async act(g, action) {
      this.busy = true;
      try {
        await this.callUpdate(g.id, action);
        this.notify(`Grant ${action}d.`);
        await this.load();
      } catch (err) {
        console.error(`updateGuestGrant ${action} failed:`, err);
        this.notify(this.friendlyError(err), "error");
      } finally {
        this.busy = false;
      }
    },
    async callUpdate(grantId, action, value) {
      const updateGuestGrant = httpsCallable(functions, "updateGuestGrant");
      await updateGuestGrant({ grantId, action, value });
    },
    friendlyError(err) {
      return (
        err?.message?.replace(/^Firebase: /, "") ||
        "Action failed. Please try again."
      );
    },
    async copyInvite(g) {
      const title = g.projectTitle || "our project";
      const text =
        `You've been invited to collaborate on "${title}" in Alice, ` +
        `Valsplat's research workspace.\n\n` +
        `1. Go to ${window.location.origin}/guest\n` +
        `2. Enter this email address (${g.email})\n` +
        `3. Open the sign-in link you receive — you're in.\n\n` +
        `Your access runs until ${this.formatDate(g.expiresAt)}.`;
      try {
        await navigator.clipboard.writeText(text);
        this.copiedId = g.id;
        setTimeout(() => (this.copiedId = null), 2000);
      } catch (err) {
        console.error("clipboard write failed:", err);
      }
    },
    notify(text, color = "success") {
      this.snackbar = { show: true, text, color };
    },
  },
};
</script>

<style scoped lang="scss">
.alice-gst {
  padding: 32px 40px;
  overflow-y: auto;
  height: 100%;

  &__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 28px;
  }

  &__crumb {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    font-family: var(--font-ui);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--fg-3);
  }

  &__title {
    margin: 0 0 8px;
    font-family: var(--font-display);
    font-size: 30px;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--vp-black);
  }

  &__count {
    font-size: 16px;
    font-weight: 600;
    color: var(--fg-3);
    margin-left: 6px;
  }

  &__subtitle {
    margin: 0;
    max-width: 560px;
    font-family: var(--font-body);
    font-size: 14px;
    line-height: 1.55;
    color: var(--fg-2);
  }

  &__refresh {
    padding: 8px 16px;
    background: var(--vp-white);
    border: 1px solid var(--vp-black);
    border-radius: 0;
    cursor: pointer;
    font-family: var(--font-ui);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--vp-black);

    &:hover:not(:disabled) {
      background: var(--vp-black);
      color: var(--vp-white);
    }
    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }

  &__state {
    padding: 48px 0;
    font-family: var(--font-body);
    font-size: 15px;
    color: var(--fg-3);

    &--error {
      color: var(--vp-rood-100, #d43c3c);
    }
  }

  &__table {
    border: 1px solid var(--border-hairline);
    background: var(--vp-white);
  }

  &__row {
    display: grid;
    grid-template-columns: 1.4fr 1.2fr 0.8fr 0.9fr 1.1fr 0.7fr 1.6fr;
    gap: 12px;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid var(--border-hairline);
    font-family: var(--font-body);
    font-size: 14px;
    color: var(--fg-1);

    &--head {
      font-family: var(--font-ui);
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--fg-3);
      background: var(--bg-muted);
    }
  }

  &__email,
  &__project,
  &__inviter {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__project a {
    color: inherit;
  }

  &__sub {
    font-family: var(--font-ui);
    font-size: 12px;
    color: var(--fg-3);
  }

  &__usage-bar {
    height: 6px;
    background: var(--bg-muted);
    border: 1px solid var(--border-hairline);
    position: relative;
    margin-bottom: 4px;
  }

  &__usage-fill {
    position: absolute;
    inset: 0 auto 0 0;
    background: var(--vp-turquoise-100, #00a8a8);

    &--full {
      background: var(--vp-rood-100, #d43c3c);
    }
  }

  &__status {
    font-family: var(--font-ui);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--fg-3);

    &--active {
      color: var(--vp-turquoise-100, #00a8a8);
    }
    &--invited {
      color: var(--vp-geel-110, #b58a00);
    }
    &--disabled,
    &--revoked,
    &--expired {
      color: var(--vp-rood-100, #d43c3c);
    }
  }

  &__actions {
    display: flex;
    gap: 6px;
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  &__action {
    padding: 4px 8px;
    background: var(--vp-white);
    border: 1px solid var(--border-hairline);
    border-radius: 0;
    cursor: pointer;
    font-family: var(--font-ui);
    font-size: 12px;
    color: var(--vp-black);
    display: inline-flex;
    align-items: center;
    gap: 4px;

    &:hover {
      border-color: var(--vp-black);
    }

    &--danger:hover {
      border-color: var(--vp-rood-100, #d43c3c);
      color: var(--vp-rood-100, #d43c3c);
    }
  }

  &__edit {
    display: flex;
    align-items: flex-end;
    gap: 16px;
    padding: 12px 16px;
    background: var(--bg-muted);
    border-bottom: 1px solid var(--border-hairline);
  }

  &__edit-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-family: var(--font-ui);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--fg-3);

    input {
      padding: 6px 10px;
      border: 1px solid var(--border-hairline);
      background: var(--vp-white);
      font-family: var(--font-body);
      font-size: 14px;
      color: var(--vp-black);
      outline: none;

      &:focus {
        border-color: var(--vp-black);
      }
    }
  }

  &__edit-save {
    padding: 7px 16px;
    background: var(--vp-black);
    color: var(--vp-white);
    border: 1px solid var(--vp-black);
    cursor: pointer;
    font-family: var(--font-ui);
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }

  &__edit-cancel {
    padding: 7px 16px;
    background: var(--vp-white);
    border: 1px solid var(--border-hairline);
    cursor: pointer;
    font-family: var(--font-ui);
    font-size: 12px;
    color: var(--vp-black);
  }
}
</style>
